import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STORAGE_KEY = "gfwjc_exit_intent_shown_v1";
const PDF_URL = "/kickstart-7day.pdf";

// Shows a lead-capture modal when a visitor tries to leave the tab (desktop mouse
// exits to top) or after a delay + scroll depth on mobile. Once shown or dismissed,
// stays hidden for 7 days via localStorage.
export default function ExitIntent() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Respect previous dismissal / submission (7 days)
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000) return;
      }
    } catch (_e) { /* ignore storage errors */ }

    let armed = false;
    // Arm after user shows real engagement (scrolled 25% or 8s passed)
    const armTimer = setTimeout(() => { armed = true; }, 8000);
    const onScrollArm = () => {
      if (window.scrollY > window.innerHeight * 0.25) armed = true;
    };
    window.addEventListener("scroll", onScrollArm, { passive: true });

    const trigger = () => {
      if (!armed) return;
      setOpen(true);
      cleanup();
    };

    const onMouseOut = (e) => {
      // Desktop: mouse leaves through top of viewport
      if (window.innerWidth < 768) return;
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) trigger();
    };
    document.addEventListener("mouseout", onMouseOut);

    // Mobile fallback: after 30s of scrolling deep
    const mobileTimer = setTimeout(() => {
      if (window.innerWidth < 768 && armed) trigger();
    }, 30000);

    function cleanup() {
      clearTimeout(armTimer);
      clearTimeout(mobileTimer);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScrollArm);
    }
    return cleanup;
  }, []);

  const remember = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
  };

  const close = () => {
    remember();
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: "Kickstart Download",
        email,
        phone: "",
        program: "bootcamp",
        goal: "Downloaded 7-Day Kickstart PDF via exit-intent popup",
      });
      setDone(true);
      remember();
      toast("PDF unlocked — check your email or download now.");
      // Auto-open the PDF in a new tab
      window.open(PDF_URL, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="exit-intent-overlay"
          onClick={close}
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            className="relative w-full max-w-lg bg-[#0a0a0a] border border-white/10 p-8 sm:p-10"
            onClick={(e) => e.stopPropagation()}
            data-testid="exit-intent-modal"
          >
            <button
              onClick={close}
              aria-label="Close"
              data-testid="exit-intent-close"
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            {!done ? (
              <>
                <div className="font-mono-accent text-[10px] tracking-[0.3em] text-[#005EEA] mb-4">
                  — WAIT.
                </div>
                <h3 className="font-display text-3xl sm:text-4xl uppercase leading-[0.95] mb-4">
                  Grab the <span className="text-[#005EEA]">7-Day Kickstart</span> before you go.
                </h3>
                <p className="text-white/60 text-sm sm:text-base mb-6">
                  A free workout + nutrition primer to start your transformation this week.
                  No spam. Just the plan.
                </p>
                <form onSubmit={submit} className="space-y-3" data-testid="exit-intent-form">
                  <div className="relative">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full bg-black border border-white/15 pl-11 pr-4 py-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#005EEA] transition-colors"
                      data-testid="exit-intent-email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="exit-intent-submit"
                    className="w-full font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white py-4 hover:bg-[#0047b3] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Download size={15} /> {submitting ? "Sending..." : "Send Me the PDF"}
                  </button>
                  <p className="text-[10px] text-white/40 text-center">
                    By signing up you agree to receive fitness tips from Getfitwithjeanclaude.
                    Unsubscribe anytime.
                  </p>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="font-mono-accent text-[10px] tracking-[0.3em] text-[#005EEA] mb-4">
                  — LOCKED IN.
                </div>
                <h3 className="font-display text-3xl uppercase leading-tight mb-3">
                  You&apos;re in.
                </h3>
                <p className="text-white/60 text-sm mb-6">
                  Your Kickstart PDF should open in a new tab. Check your email for the link too.
                </p>
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.2em] border border-white/20 text-white px-6 py-3 hover:border-white transition-colors"
                  data-testid="exit-intent-download"
                >
                  <Download size={14} /> Download Again
                </a>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
